import { CARD_STATUSES } from './cardStatus';

export const PRIORITIES = ['urgent', 'high', 'medium', 'low'];

export const DEFAULT_FILTERS = {
  typeIds: [],
  assignee: 'all',
  priorities: [],
  stageIds: [],
  overdue: false,
  status: 'open',
  search: '',
  // Which board is shown; null lets the board pick (see CardsIndex).
  pipelineId: null,
};

const toList = value => (value ? String(value).split(',') : []);
const toIds = value => toList(value).map(Number);

// The sidebar links each board to /cards/board/:pipelineId, so the board lives in the path (and
// keeps its sidebar entry highlighted); every filter lives in the query string.
export const parseFilters = ({ params, query }) => ({
  typeIds: toIds(query.types),
  assignee: query.assignee || DEFAULT_FILTERS.assignee,
  priorities: toList(query.priorities),
  stageIds: toIds(query.stages),
  overdue: query.overdue === '1',
  status: CARD_STATUSES.includes(query.status) ? query.status : 'open',
  search: query.search || '',
  pipelineId: Number(params.pipelineId) || null,
});

export const filtersToRoute = (filters, accountId) => {
  const query = {};
  if (filters.typeIds.length) query.types = filters.typeIds.join(',');
  if (filters.assignee !== 'all') query.assignee = filters.assignee;
  if (filters.priorities.length)
    query.priorities = filters.priorities.join(',');
  if (filters.stageIds.length) query.stages = filters.stageIds.join(',');
  if (filters.overdue) query.overdue = '1';
  if (filters.status !== 'open') query.status = filters.status;
  if (filters.search) query.search = filters.search;

  return filters.pipelineId
    ? {
        name: 'cards_pipeline_index',
        params: { accountId, pipelineId: filters.pipelineId },
        query,
      }
    : { name: 'cards_index', params: { accountId }, query };
};

// The search and the board are left out: the search has its own control, the board its sidebar entry.
export const countActiveFilters = filters =>
  [
    filters.typeIds.length,
    filters.assignee !== 'all',
    filters.priorities.length,
    filters.stageIds.length,
    filters.overdue,
    filters.status !== 'open',
  ].filter(Boolean).length;

// The stage filter is not sent: it decides which columns the board shows, and each column
// already asks for its own stage (which also pins the pipeline).
export const filtersToParams = (filters, currentUserId) => {
  const params = {};
  if (filters.typeIds.length) params.card_task_type_id = filters.typeIds;
  if (filters.priorities.length) params.priority = filters.priorities;
  if (filters.assignee === 'me') params.assignee_id = currentUserId;
  if (filters.assignee === 'unassigned') params.unassigned = true;
  if (filters.overdue) params.overdue = true;
  if (filters.status !== 'open') params.status = filters.status;
  if (filters.search) params.q = filters.search;
  return params;
};

// The conversation panel holds every card of one conversation, so it filters in the browser.
// A card's type is the type of the task that opened it, the one its header shows.
export const matchesFilters = (card, filters, currentUserId) => {
  const assigneeIds = card.assignees.map(assignee => assignee.id);
  const isOverdue =
    card.status === 'open' &&
    !!card.due_at &&
    new Date(card.due_at) < new Date();

  return (
    (!filters.typeIds.length ||
      filters.typeIds.includes(card.task_summary.first?.type_id)) &&
    (!filters.priorities.length ||
      filters.priorities.includes(card.priority)) &&
    (filters.assignee !== 'me' || assigneeIds.includes(currentUserId)) &&
    (filters.assignee !== 'unassigned' || !assigneeIds.length) &&
    (!filters.overdue || isOverdue)
  );
};
